import { useEffect, useRef, useState, useCallback } from 'react';
import { usePoseDetection3D, POSE_LANDMARKS } from './usePoseDetection3D';
import { computeMobilityMeasurement, metricAngle, metricVisible } from '../lib/mobilityRom';

// ── Capture tuning (the two independent dials) ──────────────────────────────
// 1) DENOISE — a short MEDIAN (not average) kills single-frame jitter without
//    lag or pulling the value toward zero, so the true end-range is preserved.
const SMOOTH_N = 5;            // ~0.4s median window on the live angle
// 2) "REAL MOVEMENT" — these decide what actually counts as range; firm so a
//    small wobble never scores. (min_rom is per-test; this is the floor.)
const DEFAULT_MIN_ROM = 12;

const HOLD_MS = 1800;          // end-range must stay flat this long to capture
const HOLD_BAND_DEG = 10;      // "flat" = the held range stays within this spread
const VIS_MIN = 0.45;
const NEUTRAL_MIN_FRAMES = 6;
const NEUTRAL_MIN_MS = 700;    // settle neutral after this if we have enough frames…
const NEUTRAL_MAX_MS = 2500;   // …but never block longer than this (take median anyway)
const POSITION_GATE_MS = 800;

const median = (arr) => {
  const s = arr.filter((v) => v != null).sort((x, y) => x - y);
  const n = s.length;
  if (!n) return null;
  return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
};

const PHASES = {
  IDLE: 'idle',
  POSITIONING: 'positioning',
  READY: 'ready',
  MEASURING: 'measuring',
  DONE: 'done',
  ERROR: 'error',
};

/**
 * useMobilityTracker (v3.2) — reliable camera ROM engine.
 *
 *  • Live angle is MEDIAN-smoothed (denoise) but min_rom stays firm (no false
 *    scores from small movements).
 *  • Neutral is captured forgivingly (median over a short window, with a hard
 *    timeout) so it never gets stuck "finding your neutral".
 *  • Capture is a PLATEAU detector: lock only when the smoothed ROM has stayed
 *    flat AND above min_rom for HOLD_MS — robust to jitter and to "still going
 *    deeper" (it just waits until you settle).
 *  • Auto-side tests (hip, ankle) measure whichever limb is actually working.
 *  • Visibility-aware: frames where the chosen landmarks aren't seen are ignored.
 */
export const useMobilityTracker = () => {
  const pose = usePoseDetection3D(true);

  const [phase, setPhase] = useState(PHASES.IDLE);
  const [primaryAngle, setPrimaryAngle] = useState(null);
  const [romValue, setRomValue] = useState(null);
  const [neutralAngle, setNeutralAngle] = useState(null);
  const [peakValue, setPeakValue] = useState(null);
  const [guardViolated] = useState(false);
  const [guardPeakViolation] = useState(null);
  const [currentSide, setCurrentSide] = useState(null);
  const [message, setMessage] = useState('Initialising camera…');
  const [gateProgress, setGateProgress] = useState(0);
  const [holdProgress, setHoldProgress] = useState(0);

  const testDefRef = useRef(null);
  const sideRef = useRef(null);
  const phaseRef = useRef(PHASES.IDLE);
  const candRef = useRef([]);
  const gateHoldStartRef = useRef(null);
  const readyStartRef = useRef(null);
  const measureStartRef = useRef(null);
  const neutralBufRef = useRef({});        // { side: [angle] } while finding neutral
  const neutralFrozenRef = useRef({});     // { side: angle }
  const neutralReadyRef = useRef(false);
  const chosenTripletRef = useRef(null);
  const chosenSideRef = useRef(null);
  const chosenNeutralRef = useRef(null);
  const smoothBufRef = useRef([]);         // recent raw angles of the chosen limb (for live median)
  const romHistRef = useRef([]);           // [{t, rom}] smoothed ROM history (plateau detection)
  const peakRomRef = useRef(0);
  const resultRef = useRef(null);
  const frameBufRef = useRef([]);

  const cfgFor = useCallback((td, side) => {
    if (!td) return null;
    const pm = td.primary_metric;
    const autoSide = !!pm.auto_side;
    const declaredTrip = side === 'R' ? pm.triplet_R : pm.triplet_L;
    const candidates = autoSide
      ? [{ side: 'L', triplet: pm.triplet_L }, { side: 'R', triplet: pm.triplet_R }]
      : [{ side, triplet: declaredTrip }];
    const minRom = pm.min_rom_deg ?? DEFAULT_MIN_ROM;
    return {
      pm,
      candidates,
      dir: pm.phase === 'peak' ? 1 : -1,
      minRom,
      startTrigger: Math.min(14, Math.max(8, minRom)),
      durationSec: td.duration_seconds || 5,
      sideSetup: td.side_setup_instruction?.[side] || '',
    };
  }, []);

  const goPhase = useCallback((p, msg) => {
    phaseRef.current = p;
    setPhase(p);
    if (msg != null) setMessage(msg);
  }, []);

  // Median of the last SMOOTH_N raw angles → denoised live value.
  const smoothPush = (a) => {
    const b = smoothBufRef.current;
    b.push(a);
    if (b.length > SMOOTH_N) b.shift();
    return median(b);
  };

  const captureFromBuffer = useCallback((td, side) => {
    const buf = frameBufRef.current;
    let frames = buf.slice();
    if (frames.length < 5) frames = buf.slice(-18);
    let m = null;
    try {
      m = computeMobilityMeasurement(frames, td, side, {
        neutral: chosenNeutralRef.current,
        triplet: chosenTripletRef.current,
      });
    } catch (e) { m = null; }
    const fallbackRom = peakRomRef.current != null ? Math.round(peakRomRef.current * 10) / 10 : null;
    resultRef.current = {
      side,
      raw_value: (m && m.raw_value != null) ? m.raw_value : fallbackRom,
      neutral_value: m ? m.neutral_value : (chosenNeutralRef.current != null ? Math.round(chosenNeutralRef.current * 10) / 10 : null),
      peak_value: m ? m.peak_value : null,
      guard_violated: m ? m.guard_violated : false,
      guard_peak_violation: m ? m.guard_peak_violation : null,
      measured_side: chosenSideRef.current,
    };
    setHoldProgress(1);
    goPhase(PHASES.DONE, 'Measurement captured.');
  }, [goPhase]);

  useEffect(() => {
    const lm2D = pose.landmarks2D;
    const td = testDefRef.current;
    const side = sideRef.current;
    if (!td || !side || !lm2D) return;
    const cfg = cfgFor(td, side);
    if (!cfg) return;
    const now = performance.now();
    const { pm, candidates, dir } = cfg;

    const visibleCands = candidates.filter((c) => metricVisible(lm2D, pm, c.triplet));

    // 1. POSITIONING — wait until the body is in frame for POSITION_GATE_MS.
    if (phaseRef.current === PHASES.POSITIONING) {
      if (visibleCands.length > 0) {
        if (!gateHoldStartRef.current) gateHoldStartRef.current = now;
        const held = now - gateHoldStartRef.current;
        setGateProgress(Math.min(1, held / POSITION_GATE_MS));
        if (held >= POSITION_GATE_MS) {
          gateHoldStartRef.current = null;
          setGateProgress(1);
          neutralBufRef.current = {};
          neutralFrozenRef.current = {};
          neutralReadyRef.current = false;
          readyStartRef.current = now;
          goPhase(PHASES.READY, 'Hold still a second — finding your neutral…');
        }
      } else {
        gateHoldStartRef.current = null;
        setGateProgress(0);
        setMessage('Step back so your whole body is in frame.');
      }
      return;
    }

    // 2. READY — (a) lock a forgiving neutral, then (b) wait for real movement.
    if (phaseRef.current === PHASES.READY) {
      if (visibleCands.length === 0) {
        setMessage('Step back so your whole body is in frame.');
        return;
      }

      if (!neutralReadyRef.current) {
        for (const c of visibleCands) {
          const ang = metricAngle(lm2D, pm, c.triplet);
          if (ang == null) continue;
          const buf = neutralBufRef.current[c.side] || (neutralBufRef.current[c.side] = []);
          buf.push(ang);
          if (buf.length > 40) buf.shift();
        }
        setPrimaryAngle(metricAngle(lm2D, pm, visibleCands[0].triplet));

        const elapsed = now - (readyStartRef.current || now);
        const enough = visibleCands.every((c) => (neutralBufRef.current[c.side]?.length || 0) >= NEUTRAL_MIN_FRAMES);
        if ((enough && elapsed >= NEUTRAL_MIN_MS) || elapsed >= NEUTRAL_MAX_MS) {
          for (const c of visibleCands) {
            const samples = neutralBufRef.current[c.side];
            if (samples?.length) neutralFrozenRef.current[c.side] = median(samples);
          }
          if (Object.keys(neutralFrozenRef.current).length) {
            neutralReadyRef.current = true;
            const disp = visibleCands.find((c) => c.side === side) || visibleCands[0];
            setNeutralAngle(neutralFrozenRef.current[disp.side]);
            goPhase(PHASES.READY, 'Neutral locked. Move into your range when ready.');
          }
        } else {
          setMessage('Hold still — finding your neutral…');
        }
        return;
      }

      // Neutral locked → detect a real movement on the working limb.
      let best = null, bestRom = 0;
      for (const c of visibleCands) {
        const neu = neutralFrozenRef.current[c.side];
        if (neu == null) continue;
        const ang = metricAngle(lm2D, pm, c.triplet);
        if (ang == null) continue;
        const rom = Math.max(0, dir * (ang - neu));
        if (rom > bestRom) { bestRom = rom; best = { c, neu }; }
      }
      if (best) { setPrimaryAngle(metricAngle(lm2D, pm, best.c.triplet)); setRomValue(Math.round(bestRom)); }
      if (best && bestRom >= cfg.startTrigger) {
        chosenTripletRef.current = best.c.triplet;
        chosenSideRef.current = best.c.side;
        chosenNeutralRef.current = best.neu;
        setNeutralAngle(best.neu);
        measureStartRef.current = now;
        peakRomRef.current = bestRom;
        smoothBufRef.current = [];
        romHistRef.current = [];
        frameBufRef.current = [];
        setHoldProgress(0);
        goPhase(PHASES.MEASURING, 'Reach into your end range and hold it steady…');
      }
      return;
    }

    // 3. MEASURING — denoise, then capture on a stable end-range PLATEAU.
    if (phaseRef.current === PHASES.MEASURING) {
      const trip = chosenTripletRef.current;
      const rawA = metricAngle(lm2D, pm, trip);
      if (rawA == null) return;            // chosen limb not visible this frame → skip
      const a = smoothPush(rawA);          // median-denoised live angle
      setPrimaryAngle(a);

      frameBufRef.current.push({ landmarks2D: lm2D, t: now });
      const cutoff = now - 10000;
      while (frameBufRef.current.length && frameBufRef.current[0].t < cutoff) frameBufRef.current.shift();

      const neutral = chosenNeutralRef.current;
      const rom = neutral != null ? Math.max(0, dir * (a - neutral)) : Math.abs(a);
      setRomValue(Math.round(rom));
      if (rom > peakRomRef.current) peakRomRef.current = rom;
      setPeakValue(Math.round(peakRomRef.current));

      const hist = romHistRef.current;
      hist.push({ t: now, rom });
      while (hist.length && hist[0].t < now - 5000) hist.shift();

      // Stable suffix: walk back from newest while the range stays within the
      // band. Its time-span = how long you've held this end-range.
      let mn = Infinity, mx = -Infinity, t0 = now;
      const windowRoms = [];
      for (let i = hist.length - 1; i >= 0; i--) {
        const r = hist[i].rom;
        const nmn = Math.min(mn, r), nmx = Math.max(mx, r);
        if (nmx - nmn > HOLD_BAND_DEG) break;
        mn = nmn; mx = nmx; t0 = hist[i].t;
        windowRoms.push(r);
      }
      const stableMs = now - t0;
      const windowMed = median(windowRoms) ?? 0;
      const atRealEndRange = windowMed >= cfg.minRom;        // firm: small movements never qualify

      if (atRealEndRange) {
        setHoldProgress(Math.min(1, stableMs / HOLD_MS));
        if (stableMs >= HOLD_MS) { captureFromBuffer(td, side); return; }
      } else {
        setHoldProgress(0);
        const elapsed0 = (now - (measureStartRef.current || now)) / 1000;
        if (elapsed0 > 2.0 && peakRomRef.current < cfg.minRom) {
          setMessage('Go further into your range and hold the end position…');
        }
      }

      const elapsed = (now - (measureStartRef.current || now)) / 1000;
      if (elapsed > cfg.durationSec + 15) {
        if (peakRomRef.current >= cfg.minRom) {
          captureFromBuffer(td, side);
        } else {
          smoothBufRef.current = [];
          romHistRef.current = [];
          frameBufRef.current = [];
          peakRomRef.current = 0;
          measureStartRef.current = null;
          setHoldProgress(0);
          goPhase(PHASES.READY, "Didn't catch a clear movement — raise into your range and hold at the end.");
        }
      }
      return;
    }
  }, [pose.landmarks2D, cfgFor, goPhase, captureFromBuffer]);

  const startCamera = useCallback(async () => {
    setMessage('Starting camera…');
    await pose.startCamera('user');
  }, [pose]);

  const resetState = useCallback(() => {
    neutralBufRef.current = {};
    neutralFrozenRef.current = {};
    neutralReadyRef.current = false;
    chosenTripletRef.current = null;
    chosenSideRef.current = null;
    chosenNeutralRef.current = null;
    smoothBufRef.current = [];
    romHistRef.current = [];
    peakRomRef.current = 0;
    gateHoldStartRef.current = null;
    readyStartRef.current = null;
    measureStartRef.current = null;
    resultRef.current = null;
    frameBufRef.current = [];
  }, []);

  const startTest = useCallback((testDef, side) => {
    testDefRef.current = testDef;
    sideRef.current = side;
    candRef.current = cfgFor(testDef, side)?.candidates || [];
    setCurrentSide(side);
    setPrimaryAngle(null);
    setRomValue(null);
    setNeutralAngle(null);
    setPeakValue(null);
    setGateProgress(0);
    setHoldProgress(0);
    resetState();
    const cfg = cfgFor(testDef, side);
    goPhase(PHASES.POSITIONING, cfg?.sideSetup || `Get into position — ${testDef.name}`);
  }, [cfgFor, goPhase, resetState]);

  const captureNow = useCallback(() => {
    if (phaseRef.current !== PHASES.MEASURING) return;
    const td = testDefRef.current, side = sideRef.current;
    if (!td || !side) return;
    captureFromBuffer(td, side);
  }, [captureFromBuffer]);

  const consumeResult = useCallback(() => {
    const r = resultRef.current;
    resultRef.current = null;
    return r;
  }, []);

  const reset = useCallback(() => {
    testDefRef.current = null;
    sideRef.current = null;
    setCurrentSide(null);
    setPrimaryAngle(null);
    setRomValue(null);
    setNeutralAngle(null);
    setPeakValue(null);
    setGateProgress(0);
    setHoldProgress(0);
    resetState();
    goPhase(PHASES.IDLE, '');
  }, [goPhase, resetState]);

  return {
    videoRef: pose.videoRef,
    canvasRef: pose.canvasRef,
    cameraActive: pose.cameraActive,
    initLoading: pose.isLoading,
    cameraError: pose.error,
    landmarks2D: pose.landmarks2D,
    landmarks3D: pose.landmarks3D,

    phase, message, currentSide, gateProgress, holdProgress,
    primaryAngle, romValue, neutralAngle, peakValue, guardViolated, guardPeakViolation,

    startCamera, startTest, captureNow, consumeResult, reset,
    stopCamera: pose.stopCamera,

    PHASES,
    POSE_LANDMARKS,
  };
};

export default useMobilityTracker;
